import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import ReactApexChart from 'react-apexcharts';

const SUPPORTED_CHART_TYPES = new Set(['line', 'bar', 'area', 'donut', 'radar']);

function normalizeAxisSeries(rawSeries) {
  if (!Array.isArray(rawSeries) || !rawSeries.length) return null;

  const allNumeric =
    rawSeries.length > 0 &&
    rawSeries.every(
      (s) =>
        typeof s === 'number' ||
        (typeof s === 'string' && s.trim() !== '' && !Number.isNaN(Number(s)))
    );

  if (allNumeric) {
    const data = rawSeries.map((n) => Number(n) || 0);
    if (!data.length) return null;
    return [{ name: 'Value', data }];
  }

  const normalizedSeries = rawSeries
    .map((s, i) => {
      if (typeof s === 'number') return { name: `Series ${i + 1}`, data: [s] };
      if (Array.isArray(s)) return { name: `Series ${i + 1}`, data: s.map((n) => Number(n) || 0) };
      if (s && typeof s === 'object' && Array.isArray(s.data)) {
        const data = s.data.map((n) => Number(n) || 0);
        if (!data.length) return null;
        return { name: s.name || `Series ${i + 1}`, data };
      }
      return null;
    })
    .filter((s): s is { name: string; data: number[] } => s != null);

  return normalizedSeries.length ? normalizedSeries : null;
}

function alignCategories(categories, dataLength) {
  const cats = Array.isArray(categories) ? categories.map(String) : [];
  const len = Math.max(dataLength, cats.length, 1);
  const aligned = [...cats];
  while (aligned.length < len) aligned.push(String(aligned.length + 1));
  return aligned.slice(0, len);
}

function normalizeChartSpec(raw) {
  if (!raw || typeof raw !== 'object') return null;
  let type = String(raw.type || '').toLowerCase();
  if (type === 'pie') type = 'donut';
  if (!SUPPORTED_CHART_TYPES.has(type)) return null;

  if (type === 'donut') {
    if (!Array.isArray(raw.series) || !raw.series.length) return null;
    const series = raw.series.map((n) => Number(n) || 0);
    if (!series.some((n) => n > 0)) return null;
    let labels = Array.isArray(raw.labels)
      ? raw.labels.map(String)
      : series.map((_, i) => `Item ${i + 1}`);
    while (labels.length < series.length) labels.push(`Item ${labels.length + 1}`);
    labels = labels.slice(0, series.length);
    return { type: 'donut', title: raw.title || '', series, labels };
  }

  const normalizedSeries = normalizeAxisSeries(raw.series);
  if (!normalizedSeries?.length) return null;

  const maxDataLen = Math.max(...normalizedSeries.map((s) => s.data.length));
  const categories = alignCategories(raw.categories, maxDataLen);

  return {
    type,
    title: raw.title || '',
    series: normalizedSeries,
    categories,
    xAxisLabel: raw.xAxisLabel || '',
    yAxisLabel: raw.yAxisLabel || '',
  };
}

function buildAxisChartOptions(chart) {
  const options: any = {
    chart: {
      background: 'transparent',
      toolbar: { show: false },
      animations: { enabled: true, speed: 400 },
      fontFamily: 'Manrope, sans-serif',
    },
    theme: { mode: 'dark' },
    colors: ['#7c3aed', '#06b6d4', '#cf2e2e', '#16a34a', '#f59e0b'],
    grid: { borderColor: '#374151', strokeDashArray: 3 },
    title: {
      text: chart.title || '',
      style: { fontSize: '12px', fontWeight: '600', color: '#e5e7eb' },
    },
    tooltip: { theme: 'dark' },
    dataLabels: { enabled: false },
    legend: { show: chart.series.length > 1 },
    xaxis: { categories: chart.categories, title: { text: chart.xAxisLabel || '' } },
    yaxis: { title: { text: chart.yAxisLabel || '' } },
  };

  if (chart.type === 'line' || chart.type === 'area') {
    options.stroke = { curve: 'smooth', width: 2 };
  }
  if (chart.type === 'area') {
    options.fill = { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0 } };
  }
  if (chart.type === 'radar') {
    options.plotOptions = {
      radar: {
        polygons: {
          strokeColors: '#374151',
          fill: { colors: ['#1f2937'] },
        },
      },
    };
  }

  return options;
}

class ApexChartBoundary extends React.Component<{ children: React.ReactNode }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

export default function VantiChart({ spec }: any) {
  const chart = useMemo(() => normalizeChartSpec(spec), [spec]);

  const baseOptions = useMemo(
    () => ({
      chart: {
        background: 'transparent',
        toolbar: { show: false },
        animations: { enabled: true, speed: 400 },
        fontFamily: 'Manrope, sans-serif',
      },
      theme: { mode: 'dark' },
      colors: ['#7c3aed', '#06b6d4', '#cf2e2e', '#16a34a', '#f59e0b'],
      title: {
        text: chart?.title || '',
        style: {
          fontSize: '12px',
          fontWeight: '600',
          color: '#e5e7eb',
        },
      },
      tooltip: { theme: 'dark' },
      dataLabels: { enabled: false },
    }),
    [chart?.title]
  );

  const axisOptions = useMemo(
    () => (chart && chart.type !== 'donut' ? buildAxisChartOptions(chart) : null),
    [chart]
  );

  if (typeof window === 'undefined' || !chart) return null;

  const chartKey = `${chart.type}-${JSON.stringify(chart.series)}`;

  if (chart.type === 'donut') {
    return (
      <motion.div
        layout={false}
        className="my-3 rounded-xl border border-dark-border bg-dark-surface p-3/60"
      >
        <ApexChartBoundary>
          <ReactApexChart
            key={chartKey}
            type="donut"
            height={220}
            width="100%"
            series={chart.series}
            options={{ ...baseOptions, labels: chart.labels, legend: { show: true } } as any}
          />
        </ApexChartBoundary>
      </motion.div>
    );
  }

  const apexType = chart.type === 'area' ? 'area' : chart.type;

  return (
    <motion.div
      layout={false}
      className="my-3 rounded-xl border border-dark-border bg-dark-surface p-3/60"
    >
      <ApexChartBoundary>
        <ReactApexChart
          key={chartKey}
          type={apexType as 'area' | 'line' | 'bar' | 'radar'}
          height={220}
          width="100%"
          series={chart.series}
          options={axisOptions as any}
        />
      </ApexChartBoundary>
    </motion.div>
  );
}
